<template>
  <div class="snapshot-panel-container">
    <!-- 列表态 -->
    <template v-if="!workSpaceTarget">
      <div class="panel-header">
        <span class="panel-title">快照列表</span>
        <div class="panel-controls">
          <el-button size="small" type="primary" icon="Plus" @click="addWorkSpaceFn" round>新增</el-button>
        </div>
      </div>
      <el-scrollbar class="snapshot-list-scrollbar">
        <div class="snapshot-entity"
          v-for="(entity, entityIndex) of dataList"
          :key="entityIndex"
          @click="workSpaceClick(entity)"
          :title="entity['name']"
        >
          <div class="entity-title ellipsis">
            <svg-icon iconName="work-space" class="entity-icon"></svg-icon>
            {{ entity['name'] }}
          </div>
          <div class="entity-control" @click.stop>
            <el-button size="small" text title="发布服务" @click="serviceUploadFn(entity)">
              <svg-icon iconName="service-upload"></svg-icon>
            </el-button>
            <el-button size="small" text title="修改" @click="editFn(entity)">
              <svg-icon iconName="el-Edit"></svg-icon>
            </el-button>
            <el-button size="small" text title="删除" @click="deleteFn(entity)">
              <svg-icon iconName="el-Delete"></svg-icon>
            </el-button>
          </div>
        </div>
        <el-empty v-if="dataList.length === 0" description="暂无快照" :image-size="60" />
      </el-scrollbar>
    </template>

    <!-- 详情态 -->
    <template v-else>
      <div class="panel-header detail-header">
        <div class="detail-header-top">
          <el-button size="small" icon="ArrowLeft" text @click="workSpaceTargetClear">返回</el-button>
          <span class="panel-title ellipsis">{{ workSpaceTarget['name'] }}</span>
        </div>
        <div class="panel-controls">
          <el-button size="small" type="primary" icon="Plus" @click="addWorkSpaceDataFn" round>新增数据</el-button>
          <el-button size="small" type="primary" icon="Refresh" :loading="workDataLoad" @click="workDataInit" round>初始化</el-button>
        </div>
      </div>
      <el-scrollbar class="snapshot-data-scrollbar">
        <div class="snapshot-data-item"
          v-for="(item) of detailDataList"
          :key="item['id']"
          @click="workSpaceDataClick(item)"
          :class="{ 'is-checked': item[checkProp] }"
          :title="item['name']"
        >
          <div class="item-title ellipsis">
            <svg-icon iconName="load-icon" v-if="item[loadingProp]" class="loading-icon"></svg-icon>
            <svg-icon :iconName="item[checkProp] ? 'check-box' : 'square'" class="check-icon"></svg-icon>
            {{ item['name'] }}
          </div>
          <div class="item-control" @click.stop>
            <el-button size="small" text title="删除" @click="deleteDataFn(item)">
              <svg-icon iconName="el-Delete"></svg-icon>
            </el-button>
          </div>
        </div>
        <el-empty v-if="detailDataList.length === 0" description="暂无数据" :image-size="60" />
      </el-scrollbar>
    </template>

    <!-- 弹窗：新增/编辑快照 -->
    <EditWorkSpace 
      v-if="editWorkSpaceShowFlag"
      :target="editWorkSpaceTarget"
      :title="editWorkSpaceTitle"
      @close="editWorkSpaceShowFlagChange(false)"
      @submitCallback="listDataInit"
    ></EditWorkSpace>

    <!-- 弹窗：发布服务 -->
    <ServiceUpload
      v-if="serviceUploadTarget"
      @close="serviceUploadTargetChange(undefined)"
      :target="serviceUploadTarget"
    ></ServiceUpload>

    <!-- 弹窗：新增数据 -->
    <WorkSpaceDataEdit 
      v-if="WorkSpaceDataEditShowFlag"
      title="新增数据"
      @close="WorkSpaceDataEditShowFlagChange(false)"
      @submitCallback="detailDataInit"
      :workSpace="workSpaceTarget"
      :AirAndWarshipList="AirAndWarshipList"
    />

    <!-- 弹窗：数据初始化 -->
    <WorkDataInit
      v-if="workDataTarget"
      title="数据初始化"
      @close="workDataTargetChange(undefined)"
      @submitCallback="workSubmitCallback"
    ></WorkDataInit>
  </div>
</template>

<script setup>
import { 
  onMounted, onUnmounted,
  ref, nextTick, toRaw,
} from 'vue';
import { message, vueCreateAppInit } from '@/utils/js/initVariable';
import store from '@/store';
import { ElMessageBox } from 'element-plus';
import SvgIcon from '@/components/SvgIcon';

// 复用现有组件
import EditWorkSpace from '@/views/Home/component/ManagePanel/components/WorkSpace/components/EditWorkSpace';
import ServiceUpload from '@/views/Home/component/ManagePanel/components/WorkSpace/components/ServiceUpload';
import WorkSpaceDataEdit from '@/views/Home/component/ManagePanel/components/WorkSpace/components/WorkSpaceData/components/WorkSpaceDataEdit';
import WorkDataInit from '@/views/Home/component/ManagePanel/components/WorkSpace/components/WorkSpaceData/components/WorkDataInit';
import CustomLocaleControl from '@/views/Home/component/ManagePanel/components/WorkSpace/components/WorkSpaceData/components/CustomLocaleControl';

import { aiComponentCreate } from '@/views/Home/component/ManagePanel/components/WorkSpace/components/WorkSpaceData/utils';

import {
  queryWorkspaceList, delWorkspace,
} from '@/api/UserWorkspaceApi';

import {
  queryUserWorkspaceDataList, delUserWorkspaceData,
  queryUserWorkspaceDataDetail, loadUserWorkspaceData,
} from '@/api/UserWorkspaceApi';

import { deleteUserWorkspaceGeoJsonDataById } from '@/api/WorkspaceApi';

import {
  queryAnnotation, saveAnnotation,
} from '@/api/UserWorkspaceDataApi';

import {
  queryRegionAnalysisResult,
} from '@/api/RegionAnalysisResultApi';

import {
  getAllAirAndWarshipType,
} from '@/api/DictApi';

// ===== Props =====
const props = defineProps({
  map: { type: Object, required: true },
});
const emit = defineEmits(['detailChange']);

// ===== 地图 =====
let rawMap, layerGroup;
const layerGroupClose = () => { try { rawMap.removeLayer(layerGroup); } catch(e) {} };
const layerGroupClear = () => { layerGroup && layerGroup.clearLayers(); };

// ===== 快照列表（列表态） =====
const dataList = ref([]);
const listDataInit = () => {
  queryWorkspaceList({}).then(res => {
    if (res.data.code == 1004) {
      dataList.value = res.data.dataList || [];
    }
  }).catch(err => { console.log(err); });
};

const workSpaceTarget = ref();
const workSpaceClick = (target) => {
  workSpaceTarget.value = target;
  detailDataInit();
  preAnnotationRequest();
  getAllAirAndWarshipTypeInit();
  store.commit('polySubmitPortChange', saveAnnotation);
  controlInit();
  emit('detailChange', true);
};
const workSpaceTargetClear = () => {
  workSpaceTarget.value = undefined;
  layerGroupClear();
  drawControlClose();
  store.commit('polySubmitPortChange', undefined);
  emit('detailChange', false);
};

// 删除快照
const deleteFn = (target) => {
  ElMessageBox.confirm('是否删除当前快照？', '删除快照', {
    confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning',
  }).then(() => {
    delWorkspace(target['id']).then(res => {
      if (res.data.code == 1004) {
        message(res.data.msg, 1);
        listDataInit();
      }
    }).catch(err => { console.log(err); });
  }).catch(() => {});
};

// 编辑快照
const editWorkSpaceTarget = ref(), editWorkSpaceTitle = ref();
const editWorkSpaceShowFlag = ref(false);
const editWorkSpaceShowFlagChange = (flag) => {
  editWorkSpaceShowFlag.value = flag;
  if (!flag) {
    editWorkSpaceTarget.value = undefined;
    editWorkSpaceTitle.value = undefined;
  }
};
const editFn = (target) => {
  editWorkSpaceTitle.value = '修改快照';
  editWorkSpaceTarget.value = Object.assign({}, target);
  editWorkSpaceShowFlagChange(true);
};
const addWorkSpaceFn = () => {
  editWorkSpaceTitle.value = '新增快照';
  editWorkSpaceShowFlagChange(true);
};

// 发布服务
const serviceUploadTarget = ref();
const serviceUploadTargetChange = (target) => { serviceUploadTarget.value = target; };
const serviceUploadFn = (target) => {
  serviceUploadTarget.value = Object.assign({}, target);
};

// ===== 快照详情（详情态） =====
const loadingProp = 'loading', checkProp = 'check';
const geojsonProp = 'geojsonData';
const detailDataList = ref([]);

const detailDataInit = () => {
  layerGroupClear();
  queryUserWorkspaceDataList(workSpaceTarget.value['id']).then(res => {
    if (res.data.code == 1004) {
      detailDataList.value = res.data.dataList || [];
    }
  }).catch(err => { console.log(err); });
  queryRegionAnalysisResultFn();
};

// 点击数据项 → 加载/卸载图层
const polygonStyle = store.getters['leafletPolygon/polygonStyle'];
const polygonMoveStyle = { color: 'rgb(234, 93, 41)' };
const delIdMap = new Map();

const workSpaceDataClick = (target) => {
  const group = target[geojsonProp];
  if (target[checkProp]) {
    group && layerGroup.removeLayer(group);
  } else {
    if (group) {
      layerGroup.addLayer(group);
    } else {
      target[loadingProp] = true;
      queryUserWorkspaceDataDetail(target['id']).then(res => {
        if (res.data.code == 1004) {
          dataLayerInit(target, res.data.data);
        }
        target[loadingProp] = false;
      }).catch(err => {
        target[loadingProp] = false;
        console.log(err);
      });
    }
  }
  target[checkProp] = !target[checkProp];
};

const dataLayerInit = (target, data) => {
  const list = data['userWorkspaceGeoJsonDataPOList'] || [];
  const group = L.featureGroup();
  const initItem = (item) => {
    const geom = JSON.parse(item['geom']);
    let layer;
    L.geoJSON(geom, {
      onEachFeature: (feature, e) => {
        if (e instanceof L.Polyline && !(e instanceof L.Polygon)) {
          e.setStyle({ weight: 4 });
        }
        layer = e;
      }
    });
    if (!layer) return;
    layer.on({
      mouseover: event => { event.target.setStyle && event.target.setStyle({ ...polygonMoveStyle }); },
      mouseout: event => { event.target.setStyle && event.target.setStyle({ ...polygonStyle }); },
      remove: event => { event.target.setStyle && event.target.setStyle({ ...polygonStyle }); },
    });
    const properties = layer['feature']['properties'];
    let text = '';
    for (let key in properties) {
      text += `<p>${key}：${properties[key]}</p>`;
    }
    text += '<button class="popup-delete-btn">删除</button>';
    layer.bindPopup(text, {
      closeButton: false, autoClose: false,
      className: "leaflet-popup-message work-space-popup", direction: 'top',
    });
    // 弹窗内删除按钮 — 通过 popupopen 事件动态绑定
    const handleDelete = () => {
      ElMessageBox.confirm('是否删除当前要素？', '删除要素', {
        confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning',
      }).then(() => {
        deleteUserWorkspaceGeoJsonDataById(item['id']).then(res => {
          if (res.data.code == 1004) {
            message(res.data.msg, 1);
            group.removeLayer(layer);
            delIdMap.delete(item['id']);
          }
        }).catch(err => { console.log(err); });
      }).catch(() => {});
    };
    layer.on('popupopen', () => {
      const popupEl = layer.getPopup()?.getElement();
      if (popupEl) {
        popupEl.querySelector('.popup-delete-btn')?.addEventListener('click', handleDelete);
      }
    });
    delIdMap.set(item['id'], layer._leaflet_id);
    layer.addTo(group);
  };
  const RICFn = (deadline) => {
    while (deadline.timeRemaining() > 0 && list.length > 0) {
      initItem(list.shift());
    }
    if (list.length === 0) {
      layerGroup.addLayer(group);
      target[geojsonProp] = group;
    } else {
      requestIdleCallback(RICFn);
    }
  };
  requestIdleCallback(RICFn);
};

// 删除数据项
const deleteDataFn = (target) => {
  ElMessageBox.confirm('是否删除当前数据？', '删除数据', {
    confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning',
  }).then(() => {
    delUserWorkspaceData(target['id']).then(res => {
      if (res.data.code == 1004) {
        message(res.data.msg, 1);
        detailDataInit();
      }
    }).catch(err => { console.log(err); });
  }).catch(() => {});
};

// 新增数据弹窗
const WorkSpaceDataEditShowFlag = ref(false);
const WorkSpaceDataEditShowFlagChange = (flag) => { WorkSpaceDataEditShowFlag.value = flag; };
const addWorkSpaceDataFn = () => { WorkSpaceDataEditShowFlagChange(true); };

// 数据初始化
const workDataLoad = ref(false);
const workDataTarget = ref(undefined);
const workDataTargetChange = (target) => {
  workDataTarget.value = target;
  if (target === undefined) workDataLoad.value = false;
};
const workDataInit = () => {
  if (workDataLoad.value) return;
  workDataLoad.value = true;
  workDataTarget.value = Object.assign({});
};
const workSubmitCallback = (target) => {
  workDataTargetChange(undefined);
  const data = { ...target, workspaceId: workSpaceTarget.value['id'] };
  Promise.all([
    loadUserWorkspaceData({
      ...data, models: ["MQ-4C", "E-3C", "E-3G", "E-737"],
      dataType: 6, dataName: 'track', trackType: 'air', classification: '侦察预警机',
    }),
    loadUserWorkspaceData({ ...data, dataType: 1, dataName: 'clueArea' }),
    loadUserWorkspaceData({ ...data, dataType: 3, dataName: 'notam' }),
    loadUserWorkspaceData({ ...data, dataType: 4, dataName: 'ntm' }),
    loadUserWorkspaceData({ ...data, dataType: 5, dataName: 'tfr' }),
  ]).then(() => {
    workDataLoad.value = false;
    detailDataInit();
  }).catch(err => {
    workDataLoad.value = false;
    console.log(err);
  });
};

// ===== 地图绘制事件 =====
const drawControlClose = () => {
  try { rawMap.off(L.Draw.Event.DRAWSTOP); } catch(e) {}
};

const controlInit = () => {
  rawMap.on(L.Draw.Event.DRAWSTOP, event => {
    if (!event.layer) return;
    const layer = event.layer;
    const bounds = layer.getBounds && layer.getBounds();
    if (!bounds || !bounds.isValid()) {
      message('无效区域', 2);
      return;
    }
    customLocaleControlInit(layer, {});
    layerGroup.addLayer(layer);
  });
};

const delPolyLayerSucceedFn = (delIds) => {
  layerGroup.getLayers().forEach(group => {
    if (!group.getLayers) return;
    delIds.reduceRight((acc, id, index) => {
      const leaflet_id = delIdMap.get(id);
      if (!leaflet_id) return;
      if (group.hasLayer(leaflet_id)) {
        group.removeLayer(leaflet_id);
        delIds.splice(index, 1);
      }
      return delIds;
    }, delIds);
  });
};

// 轨迹裁剪成功后，重新请求对应 ids 的数据并重新渲染图层
const refreshDataByIds = (dataIds) => {
  detailDataList.value.forEach(item => {
    if (!dataIds.includes(item['id']) || !item[checkProp]) return;
    if (item[geojsonProp]) {
      layerGroup.removeLayer(item[geojsonProp]);
    }
    queryUserWorkspaceDataDetail(item['id']).then(res => {
      if (res.data.code == 1004) {
        dataLayerInit(item, res.data.data);
      }
    }).catch(err => { console.log(err); });
  });
};

const customLocaleControlInit = (layer, target) => {
  const bounds = layer.getBounds();
  const layerClick = () => {
    const icon = controlMarker && controlMarker._icon;
    if (!icon) return;
    icon.style.display = icon.style.display === 'none' ? 'unset' : 'none';
  };
  const app = vueCreateAppInit(CustomLocaleControl, {
    target, layer,
    map: rawMap,
    onLayerDelete: () => { layerGroup.removeLayer(layer); },
    layerGroup,
    workSpace: workSpaceTarget.value,
    workSpaceDataList: detailDataList.value,
    onDelPolyLayerSucceedFn: delPolyLayerSucceedFn,
    onTrajectoryClipSucceed: refreshDataByIds,
    layerClick,
  });

  let isMounted = false;
  const controlMarker = L.marker(bounds.getNorthEast(), {
    icon: L.divIcon({ iconAnchor: [-5, 0] }),
  }).on({
    remove: () => { try { if (isMounted) { app.unmount(); isMounted = false; } } catch(e) {} }
  }).addTo(layerGroup);

  nextTick(() => {
    const icon = controlMarker._icon;
    if (icon) {
      icon.style.display = 'none';
      app.mount(icon);
      isMounted = true;
    }
  });
  layer.on({
    remove: () => { layerGroup.removeLayer(controlMarker); }
  });
};

// 预加载标注
const preAnnotationInit = (list) => {
  list.forEach(item => {
    if (!item['geom']) return;
    const geom = JSON.parse(item['geom']);
    let layer;
    L.geoJSON(geom, { onEachFeature: (f, e) => { layer = e; } });
    if (!layer) return;
    layer.addTo(layerGroup);
    customLocaleControlInit(layer, item);
  });
};
const preAnnotationRequest = () => {
  queryAnnotation(`3/${workSpaceTarget.value['id']}`).then(res => {
    if (res.data.code == 1004) {
      preAnnotationInit(res.data.dataList || []);
    }
  }).catch(err => { console.log(err); });
};

// AI 分析结果
const analysisResultInit = (data) => {
  data.forEach(target => {
    if (!target['startPoint']) return;
    const latlng = L.latLng(target['startPoint'].split(','));
    const { app, zoomCloseFn, group } = aiComponentCreate(
      latlng,
      { ...target, workSpaceId: workSpaceTarget.value['id'] },
      { layerGroup, map: rawMap }
    );
    layerGroup.addLayer(group);
    group.once({
      remove: () => { zoomCloseFn(); app?.unmount(); }
    });
  });
};
const queryRegionAnalysisResultFn = () => {
  queryRegionAnalysisResult(workSpaceTarget.value['id']).then(res => {
    if (res.data.code == 1004) {
      analysisResultInit(res.data.dataList || []);
    }
  }).catch(err => { console.log(err); });
};

// 飞机舰船类型
const AirAndWarshipList = ref([]);
const getAllAirAndWarshipTypeInit = () => {
  getAllAirAndWarshipType({}).then(res => {
    if (res.data.code == 1004) {
      AirAndWarshipList.value = res.data.dataList || [];
    }
  }).catch(err => { console.log(err); });
};

// ===== 生命周期 =====
onMounted(() => {
  rawMap = toRaw(props.map);
  layerGroup = L.featureGroup().addTo(rawMap);
  listDataInit();
  store.commit('isHomePageChange', false);
});
onUnmounted(() => {
  layerGroupClose();
  drawControlClose();
  store.commit('polySubmitPortChange', undefined);
  store.commit('isHomePageChange', true);
});
</script>

<style lang='less'>
.work-space-popup {
  .leaflet-popup-content {
    max-width: 420px;
    max-height: 360px;
    overflow-x: hidden;
    overflow-y: auto;
    &::-webkit-scrollbar { display: none; }
  }
  .popup-delete-btn {
    margin-top: 8px;
    padding: 4px 12px;
    cursor: pointer;
    border: 1px solid #f56c6c;
    color: #f56c6c;
    background: #fff;
    border-radius: 4px;
    font-size: 13px;
    &:hover {
      background: #fef0f0;
    }
  }
}
</style>

<style lang='less' scoped>
.snapshot-panel-container {
  height: 100%;
  display: flex;
  flex-direction: column;

  .panel-header {
    padding: 10px 12px;
    border-bottom: 1px solid var(--borderColor);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background-color: var(--domBackgroundColor);

    .panel-title {
      font-size: 15px;
      font-weight: 600;
      color: var(--fontColor_24);
      flex: 1;
      min-width: 0;
    }
    .panel-controls {
      display: flex;
      gap: 4px;
      flex-shrink: 0;
    }
  }

  .detail-header {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    .detail-header-top {
      display: flex;
      align-items: center;
      gap: 6px;
      width: 100%;
      .panel-title {
        flex: 1;
        min-width: 0;
      }
    }
    .panel-controls {
      width: 100%;
      justify-content: flex-end;
    }
  }

  .snapshot-list-scrollbar,
  .snapshot-data-scrollbar {
    flex: 1;
    :deep(.el-scrollbar__view) {
      display: flex;
      flex-direction: column;
      gap: var(--padDefaultSize);
      padding: 8px;
    }
  }

  .snapshot-entity {
    border-radius: var(--borderRadiusSize);
    padding: var(--padDefaultSize);
    backdrop-filter: blur(5px);
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    &:hover {
      background-color: var(--backgroundColor_12);
    }
    .entity-title {
      line-height: 24px;
      font-size: 14px;
      font-weight: 500;
      color: var(--fontColor_24);
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 1;
      min-width: 0;
      .entity-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        opacity: 0.6;
      }
    }
    .entity-control {
      display: flex;
      gap: 0;
      flex-shrink: 0;
      .el-button + .el-button {
        margin-left: 0;
      }
      .el-button {
        padding: 4px;
      }
      .svg-icon {
        cursor: pointer;
        width: 16px;
        height: 16px;
        &:hover {
          fill: var(--iconColor_5);
          color: var(--iconColor_5);
        }
      }
    }
  }

  .snapshot-data-item {
    border-radius: var(--borderRadiusSize);
    padding: var(--padDefaultSize);
    backdrop-filter: blur(5px);
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    &:hover {
      background-color: var(--backgroundColor_12);
    }
    .item-title {
      font-size: 13px;
      color: var(--fontColor_24);
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 1;
      min-width: 0;
      .check-icon {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
        opacity: 0.5;
      }
      .loading-icon {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
        animation: spin 1s linear infinite;
      }
    }
    .item-control {
      flex-shrink: 0;
      .svg-icon {
        cursor: pointer;
        width: 14px;
        height: 14px;
        &:hover {
          fill: var(--iconColor_5);
          color: var(--iconColor_5);
        }
      }
    }
  }

  .is-checked {
    background-color: var(--backgroundColor_12);
    .item-title {
      color: var(--iconColor_5);
      .check-icon {
        opacity: 1;
        fill: var(--iconColor_5);
      }
    }
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
